import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import {
  ProviderCancelled, ProviderUnavailable, credentialFor, isAvailable, type Provider,
} from '@/api/providers';
import {
  bumpSessions, clearNag, markNagDismissed, nagCopy, nagLevel, readDismissed,
} from '@/game/nag';
import { useSession } from '@/store/session';
import { Icon } from '@/components/ui/Icon';
import { ProviderMark } from '@/components/ui/ProviderMark';

const PROVIDERS: { id: Provider; label: string }[] = [
  { id: 'google', label: 'Google' },
  { id: 'apple', label: 'Apple' },
  { id: 'telegram', label: 'Telegram' },
];

/**
 * "Connect an account" — shown to guests, escalating with what they'd lose.
 *
 * A guest account is permanent but device-bound: it lives behind a `device_uid`
 * in localStorage, so clearing site data or changing phone loses it with no
 * recovery. This is a data-loss warning, not an upsell — see `game/nag.ts` for
 * when it fires.
 *
 * Linking keeps the same user row server-side, so the copy's promise that
 * "nothing resets" is literally true.
 */
export function LinkAccountNag() {
  const user = useSession((s) => s.user);
  const wallet = useSession((s) => s.wallet);
  const bestDepth = useSession((s) => s.bestDepth);
  const linkProvider = useSession((s) => s.linkProvider);

  /* Counted once per mount, not per render — StrictMode double-invokes effects
     in dev and would otherwise inflate every player's session count. */
  const sessions = useRef(0);
  if (sessions.current === 0) sessions.current = bumpSessions();

  const [dismissedAt, setDismissedAt] = useState(() => readDismissed());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);
  const googleMount = useRef<HTMLDivElement | null>(null);

  const input = {
    isGuest: user?.is_guest ?? false,
    sessions: sessions.current,
    coins: wallet.coins,
    kyat: wallet.kyat,
    bestDepth,
    dismissedAt,
    now: Date.now(),
  };
  const level = nagLevel(input);
  const copy = nagCopy(level, input);

  useEffect(() => { if (!input.isGuest) clearNag(); }, [input.isGuest]);

  const link = useCallback(async (provider: Provider) => {
    setError(null);
    setBusy(provider);
    try {
      const credential = await credentialFor(provider, googleMount.current ?? undefined);
      await linkProvider(provider, credential);
      clearNag();
      setLinked(true);
    } catch (e) {
      if (e instanceof ProviderCancelled) setError(null);
      else if (e instanceof ProviderUnavailable) setError(e.message);
      else if (e instanceof ApiError) setError(e.message);
      else setError('Could not connect that account. Try again.');
    } finally {
      setBusy(null);
    }
  }, [linkProvider]);

  const dismiss = useCallback(() => {
    const now = Date.now();
    markNagDismissed(now);
    setDismissedAt(now);
    setOpen(false);
  }, []);

  if (linked) {
    return (
      <div className="mx-3 mb-2 rounded-md border border-[rgba(92,203,106,.4)]
                      bg-[rgba(92,203,106,.10)] px-3 py-2.5">
        <p className="text-[11px] leading-snug text-[var(--ok)]">
          <b>Account connected.</b> Your coins, depth and collection are safe now —
          sign in with the same account on any device.
        </p>
      </div>
    );
  }

  if (!copy) return null;
  const urgent = level === 'urgent';

  return (
    <>
      <div className={`mx-3 mb-2 flex items-start gap-2.5 rounded-md border px-3 py-2.5
        ${urgent
          ? 'border-[rgba(224,85,63,.45)] bg-[rgba(224,85,63,.10)]'
          : 'border-[var(--hairline)] bg-black/25'}`}>
        <span className={`leading-none ${urgent ? 'text-[#FF9A8B]' : 'text-ink-dim'}`} aria-hidden>
          <Icon name={urgent ? 'warning' : 'link'} size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`font-pixel text-[7.5px] tracking-wide
            ${urgent ? 'text-[#FF9A8B]' : 'text-ink-dim'}`}>
            {copy.title}
          </p>
          <p className="mt-1 text-[10.5px] leading-snug text-ink-dim">{copy.body}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-sm border border-[rgba(255,220,170,.5)]
                       bg-[linear-gradient(#FFA945,#E0761C)] px-2.5 py-1
                       font-pixel text-[7px] text-[#2A1503]
                       shadow-[0_2px_0_-1px_#8C440C] active:translate-y-0.5"
          >
            {copy.action}
          </button>
          <button type="button" onClick={dismiss}
                  className="px-1 text-[9px] text-ink-dim underline">
            Later
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[70] grid place-items-end bg-black/78 p-4 backdrop-blur-sm"
             role="dialog" aria-modal="true" aria-label="Connect an account">
          <button type="button" className="absolute inset-0 cursor-default"
                  onClick={() => setOpen(false)} aria-label="Close" tabIndex={-1} />
          <div className="relative w-full rounded-lg border border-ui-lit
                          bg-[linear-gradient(#2A1B10,#1A110A)] p-4">
            <h3 className="font-pixel text-[9px] text-torch-core">CONNECT YOUR ACCOUNT</h3>
            <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
              You keep <b className="text-ink">everything</b> — same coins, same depth, same
              collection. Connecting only adds a way to sign back in from another device,
              and unlocks withdrawals.
            </p>

            <div className="mt-3 space-y-2">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => void link(p.id)}
                  disabled={busy !== null || !isAvailable(p.id)}
                  className="flex w-full items-center gap-3 rounded-sm border border-ui-lit
                             bg-[linear-gradient(#3A2718,var(--cave-floor))] px-3 py-2.5
                             text-left shadow-[0_3px_0_-1px_rgba(0,0,0,.55)]
                             active:translate-y-0.5 disabled:opacity-40 disabled:grayscale"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full
                                   bg-black/40 text-ink" aria-hidden>
                    <ProviderMark id={p.id} />
                  </span>
                  <span className="flex-1 text-[13px] font-bold text-ink">
                    {busy === p.id ? 'Connecting…' : `Continue with ${p.label}`}
                  </span>
                </button>
              ))}
            </div>
            <div ref={googleMount} className="mt-2 flex justify-center empty:hidden" />

            {error && (
              <p role="alert" className="mt-2 text-[11px] text-[#FF9A8B]">{error}</p>
            )}

            <button
              type="button"
              onClick={dismiss}
              className="mt-3 w-full rounded-sm border border-ui-lit bg-cave-floor py-2
                         font-pixel text-[7.5px] text-ink-dim"
            >
              NOT NOW
            </button>
          </div>
        </div>
      )}
    </>
  );
}
